import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, PackageOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useProductPreviewImage } from '@/hooks/useProductPreviewImage';
import { stripHtmlToText } from '@/lib/text';
import { track } from '@/lib/analytics';
import { producerProductPath } from '@/lib/paths';
import { PickupOrderDialog } from './PickupOrderDialog';
import { ProductInterestDialog } from './ProductInterestDialog';
import type { ProducerProduct } from '@/hooks/useProducerShopProducts';
import { useTranslate } from '@/i18n';

interface ProducerProductCardProps {
  product: ProducerProduct;
  producerId: string;
  producerName: string;
  producerSlug?: string | null;
  pickupInstructions?: string | null;
}

function formatPrice(amount: number, currencyCode: string = 'SEK'): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function ProducerProductCard({
  product,
  producerId,
  producerName,
  producerSlug,
  pickupInstructions,
}: ProducerProductCardProps) {
  const t = useTranslate();
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const { imageUrl: coverUrl } = useProductPreviewImage(product.id);
  const productUrl = producerProductPath(producerSlug || producerId, product.slug || product.id);

  const handleCardNavigate = () => {
    track('product_viewed', {
      product_id: product.id,
      producer_id: producerId,
      surface: 'producer_shop',
    });
  };

  const handleOrderClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product.availableForSale) return;
    setOrderDialogOpen(true);
  };

  const handleInterestClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    track('product_interest_clicked', {
      product_id: product.id,
      producer_id: producerId,
      surface: 'producer_shop',
    });

    setInterestDialogOpen(true);
  };

  return (
    <>
      <Link to={productUrl} className="block" onClick={handleCardNavigate}>
      <Card
        className="overflow-hidden border-border/50 hover:border-border transition-colors group"
        data-testid={`producer-shop-product-card-${product.slug || product.id}`}
      >
        {/* Product Image */}
        <div className="aspect-square bg-muted relative overflow-hidden">
          {(coverUrl || product.imageUrl) ? (
            <img
              src={coverUrl || product.imageUrl!}
              alt={product.imageAlt || product.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center" data-testid="product-thumb-fallback">
              <span className="text-2xl font-medium text-muted-foreground">
                {(product.title?.[0] ?? '?').toUpperCase()}
              </span>
            </div>
          )}

          {product.status === 'sold_out' && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
              <span className="text-sm font-medium text-muted-foreground">
                {t("status.soldOut")}
              </span>
            </div>
          )}
          {product.status === 'upcoming' && (
            <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
              <span className="text-sm font-medium text-foreground/70">
                {t("status.comingSoon")}
              </span>
            </div>
          )}

          {/* Package badge */}
          {(product as any).productType === 'bundle' && (
            <Badge variant="secondary" className="absolute top-2 left-2 gap-1 bg-background/90 backdrop-blur-sm text-foreground text-xs">
              <PackageOpen className="h-3 w-3" />
              Paket
            </Badge>
          )}
        </div>

        {/* Product Info */}
        <CardContent className="p-4">
          <h3 className="font-medium text-foreground line-clamp-2 mb-1 min-h-[2.5rem]">
            {product.title}
          </h3>

          {(() => {
            const descText = stripHtmlToText(product.description);
            return descText ? (
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                {descText}
              </p>
            ) : null;
          })()}

          {/* Availability line */}
          {product.status === 'sold_out' ? (
            <div className="mb-2">
              <p className="text-xs font-medium text-muted-foreground">{t("status.soldOut")}</p>
              <p className="text-xs text-muted-foreground/70">Finns igen snart</p>
            </div>
          ) : product.status === 'upcoming' ? (
            <div className="mb-2">
              <p className="text-xs font-medium text-foreground/70">{t("status.comingSoon")}</p>
              <p className="text-xs text-muted-foreground/70">Till nästa utlämning</p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground/70 mb-2">Finns i gårdsbutiken</p>
          )}

          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-foreground">
              {formatPrice(product.price, product.currencyCode)}
            </span>

            {product.status === 'sold_out' ? null : product.status === 'upcoming' ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleInterestClick}
                className="gap-1.5 px-4 py-2 min-h-[40px]"
                data-testid={`producer-shop-product-preorder-${product.slug || product.id}`}
              >
                <span className="sr-only sm:not-sr-only">Anmäl intresse</span>
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={handleOrderClick}
                disabled={!product.availableForSale}
                className="gap-1.5 px-4 py-2 min-h-[40px]"
                data-testid={`producer-shop-product-preorder-${product.slug || product.id}`}
              >
                <ShoppingBag className="h-4 w-4" />
                <span className="sr-only sm:not-sr-only">Förbeställ</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      </Link>

      <PickupOrderDialog
        open={orderDialogOpen}
        onOpenChange={setOrderDialogOpen}
        product={{
          id: product.id,
          title: product.title,
          price: product.price,
          currencyCode: product.currencyCode,
          imageUrl: coverUrl || product.imageUrl,
          pickupInstructions: product.pickupInstructions ?? null,
          stockLimit: (product as any).stockLimit ?? null,
          remaining: (product as any).remaining ?? null,
        }}
        producerId={producerId}
        producerName={producerName}
        pickupInstructions={pickupInstructions}
        sourceSurface="farm_shop_page"
      />

      <ProductInterestDialog
        open={interestDialogOpen}
        onOpenChange={setInterestDialogOpen}
        productTitle={product.title}
        productId={product.id}
        producerId={producerId}
      />
    </>
  );
}
